import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { navLinks, site } from '@/mocks/site';

interface SiteHeaderProps {
  overlay?: boolean;
}

export default function SiteHeader({ overlay = false }: SiteHeaderProps) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  const solid = !overlay || scrolled;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        solid
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200'
          : 'bg-transparent border-b border-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="h-16 md:h-20 flex items-center justify-between gap-6">
          <Link to="/" className="flex items-center gap-2.5 group shrink-0">
            <span
              className={`w-9 h-9 rounded-lg flex items-center justify-center transition-colors ${
                solid ? 'bg-primary-500 text-background-50' : 'bg-background-50/90 text-primary-600'
              }`}
            >
              <i className="ri-goblet-2-line text-lg" aria-hidden="true"></i>
            </span>
            <span className="flex flex-col leading-none">
              <span
                className={`font-logo text-lg md:text-xl tracking-wide transition-colors ${
                  solid ? 'text-foreground-950' : 'text-background-50'
                }`}
              >
                {site.brand}
              </span>
              <span
                className={`hidden sm:block text-[11px] mt-0.5 tracking-widest transition-colors ${
                  solid ? 'text-foreground-500' : 'text-background-100/80'
                }`}
              >
                {site.slogan}
              </span>
            </span>
          </Link>

          <nav aria-label="主导航" className="hidden md:flex items-center gap-1 lg:gap-2">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === '/'}
                className={({ isActive }) =>
                  `px-3.5 py-2 rounded-md text-sm whitespace-nowrap transition-colors ${
                    solid
                      ? isActive
                        ? 'text-primary-600 bg-primary-50'
                        : 'text-foreground-700 hover:text-primary-600 hover:bg-background-100'
                      : isActive
                        ? 'text-background-50 bg-background-50/20'
                        : 'text-background-100 hover:text-background-50 hover:bg-background-50/15'
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden md:flex items-center gap-3">
            <Link
              to="/topics"
              aria-label="搜索瓜料"
              className={`w-9 h-9 rounded-full flex items-center justify-center transition-colors ${
                solid
                  ? 'bg-background-100 text-foreground-700 hover:bg-background-200'
                  : 'bg-background-50/15 text-background-50 hover:bg-background-50/25'
              }`}
            >
              <i className="ri-search-line text-base" aria-hidden="true"></i>
            </Link>
            <Link
              to="/topics"
              className="px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors"
            >
              开始追瓜
            </Link>
          </div>

          <button
            type="button"
            aria-label={menuOpen ? '关闭菜单' : '打开菜单'}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((v) => !v)}
            className={`md:hidden w-10 h-10 rounded-md flex items-center justify-center transition-colors ${
              solid ? 'text-foreground-900 hover:bg-background-100' : 'text-background-50 hover:bg-background-50/15'
            }`}
          >
            <i className={`${menuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`} aria-hidden="true"></i>
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200">
          <nav aria-label="移动端导航" className="px-4 py-4 flex flex-col gap-1">
            {navLinks.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === '/'}
                className={({ isActive }) =>
                  `px-4 py-3 rounded-md text-sm transition-colors ${
                    isActive
                      ? 'bg-primary-50 text-primary-600 font-medium'
                      : 'text-foreground-700 hover:bg-background-100'
                  }`
                }
              >
                {link.label}
              </NavLink>
            ))}
            <Link
              to="/topics"
              className="mt-2 px-4 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium text-center hover:bg-primary-600 transition-colors"
            >
              开始追瓜
            </Link>
          </nav>
        </div>
      )}
    </header>
  );
}